import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { certificateAPI } from '../../services/api';
import Modal from '../../components/Modal';
import { LoadingSpinner, EmptyState } from '../../components/UI';
import { useAuth } from '../../context/AuthContext';

const MyCertificates = () => {
  const { user } = useAuth();
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await certificateAPI.mine();
        setCerts(data);
      } catch (e) {
        toast.error('Failed to load');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleDownload = async (cert) => {
    setDownloadingId(cert._id);
    try {
      const response = await certificateAPI.download(cert._id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `certificate-${cert.certificateId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Certificate downloaded');
    } catch (e) {
      toast.error('Download failed');
    } finally {
      setDownloadingId(null);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="container">
      <div className="page-header">
        <div>
          <h1>My Certificates</h1>
          <p className="subtitle">{certs.length} earned</p>
        </div>
      </div>

      {certs.length === 0 ? (
        <EmptyState
          icon="🏆"
          title="No certificates yet"
          message="Complete a course to earn your first certificate"
        />
      ) : (
        <div className="grid grid-3">
          {certs.map((c) => {
            const expired = c.expiresAt && new Date(c.expiresAt) < new Date();
            return (
              <div key={c._id} className="card">
                <div className="flex justify-between items-center">
                  <div style={{ fontSize: '2.5rem' }}>🏆</div>
                  {expired && <span className="badge badge-rejected">Expired</span>}
                </div>
                <h3 className="mt-1">{c.snapshot?.title || c.certification?.title}</h3>
                <p className="text-muted" style={{ fontSize: '0.85rem' }}>
                  Course: <strong>{c.course?.title}</strong>
                </p>
                <p className="text-muted" style={{ fontSize: '0.85rem' }}>
                  Grade: <strong>{c.grade}</strong> · Score: {c.score}%
                </p>
                <p className="text-muted" style={{ fontSize: '0.8rem' }}>
                  Issued by {c.issuedBy?.name} on {new Date(c.issuedAt).toLocaleDateString()}
                </p>
                {c.expiresAt && (
                  <p className="text-muted" style={{ fontSize: '0.8rem' }}>
                    Valid until {new Date(c.expiresAt).toLocaleDateString()}
                  </p>
                )}
                <code style={{ fontSize: '0.75rem', display: 'block', marginTop: '0.5rem' }}>
                  {c.certificateId}
                </code>
                <div className="flex gap-1 mt-2">
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => setViewing(c)}
                    style={{ flex: 1 }}
                  >
                    Preview
                  </button>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => handleDownload(c)}
                    disabled={downloadingId === c._id}
                    style={{ flex: 1 }}
                  >
                    {downloadingId === c._id ? 'Downloading…' : '⬇ PDF'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal open={!!viewing} onClose={() => setViewing(null)} title="Certificate Preview" size="lg">
        {viewing && (
          <>
            <div
              className="certificate"
              style={{
                borderColor: viewing.snapshot?.accentColor || '#92400e',
              }}
            >
              <div className="cert-label" style={{ color: viewing.snapshot?.accentColor || '#92400e' }}>
                {(viewing.snapshot?.issuingOrganization || 'HillBoat Study ').toUpperCase()}
              </div>
              <h1>Certificate of Completion</h1>
              <p className="description">This certifies that</p>
              <div className="recipient">{user?.name}</div>
              <p className="description">has successfully completed the course</p>
              <div className="course-name" style={{ color: viewing.snapshot?.accentColor || '#92400e' }}>
                "{viewing.course?.title}"
              </div>
              <p className="description" style={{ marginTop: '1rem' }}>
                Awarded the <strong>{viewing.snapshot?.title || viewing.certification?.title}</strong>{' '}
                certification with a grade of <strong>{viewing.grade}</strong> ({viewing.score}%)
              </p>
              <div className="cert-footer">
                <div>
                  <div style={{ borderTop: '1px solid #92400e', paddingTop: '0.4rem' }}>
                    Issued: {new Date(viewing.issuedAt).toLocaleDateString()}
                  </div>
                </div>
                <div>
                  <div style={{ borderTop: '1px solid #92400e', paddingTop: '0.4rem' }}>
                    {viewing.snapshot?.signatoryName || 'HillBoat Study '}
                    <div style={{ fontSize: '0.75rem', fontStyle: 'italic' }}>
                      {viewing.snapshot?.signatoryTitle}
                    </div>
                  </div>
                </div>
                <div>
                  <div style={{ borderTop: '1px solid #92400e', paddingTop: '0.4rem', fontSize: '0.75rem' }}>
                    ID: {viewing.certificateId}
                  </div>
                </div>
              </div>
            </div>
            <button
              className="btn btn-primary mt-3"
              onClick={() => handleDownload(viewing)}
              disabled={downloadingId === viewing._id}
              style={{ width: '100%' }}
            >
              {downloadingId === viewing._id ? 'Downloading…' : '⬇ Download as PDF'}
            </button>
          </>
        )}
      </Modal>
    </div>
  );
};

export default MyCertificates;
